'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { useGeoBranch, useResolvedBranch } from '@/components/SiteHostProvider';
import { getBranchData } from '@/lib/config/branches-config';
import { regionChoiceFromCookieHeader } from '@/lib/geo/geo-headers';
import { geoHintTargetUrl, rememberRegionChoice, shouldShowGeoHint } from '@/lib/geo/geo-hint';

/**
 * Ненавязчивая подсказка «Вы в Москве? Перейти на московскую версию сайта».
 * Регион посетителя — гео-IP nginx (x-geo-branch). Никаких автопереходов:
 * только по клику. Любой ответ пишет куку region_choice (180 дней), после
 * этого подсказка не показывается. Решение о показе — после mount (кука и
 * user agent доступны только в браузере), поэтому без гидрационных расхождений.
 */
export default function GeoRegionHint() {
    const geoBranch = useGeoBranch();
    const { code: currentBranch, publicPath } = useResolvedBranch();
    const router = useRouter();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        setVisible(shouldShowGeoHint({
            geoBranch,
            currentBranch,
            choice: regionChoiceFromCookieHeader(document.cookie),
            userAgent: navigator.userAgent,
        }));
    }, [geoBranch, currentBranch]);

    const target = geoBranch ? getBranchData(geoBranch) : null;
    const current = getBranchData(currentBranch);
    if (!visible || !target || !current) return null;

    const stay = () => {
        rememberRegionChoice(currentBranch);
        setVisible(false);
    };

    const go = () => {
        rememberRegionChoice(target.code);
        setVisible(false);
        const url = geoHintTargetUrl(publicPath, window.location.search, target.code, currentBranch);
        if (/^https?:\/\//.test(url)) {
            window.location.assign(url);
            return;
        }
        router.push(url);
    };

    const targetWhere = target.placeholders?.city.prepositional || `в г. ${target.name}`;
    const currentWhere = current.placeholders?.city.prepositional || `в г. ${current.name}`;
    const siteVersion = target.geo?.siteVersion || `версию сайта ${target.placeholders?.city.genitive || target.name}`;

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-labelledby="geo-region-hint-title"
            data-geo-hint={target.code}
            className="fixed bottom-[calc(132px+env(safe-area-inset-bottom))] left-2 right-2 z-40 rounded-lg border border-blue-200 bg-white p-4 shadow-xl md:bottom-4 md:left-4 md:right-auto md:w-[380px]"
        >
            <div className="flex items-start gap-3">
                <MapPinIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-600" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                    <p id="geo-region-hint-title" className="text-base font-semibold text-gray-900">
                        Вы {targetWhere}?
                    </p>
                    <p className="mt-1 text-sm text-gray-600">
                        Цены, сроки и контакты филиала {targetWhere} — на отдельной версии сайта.
                    </p>
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                        <button
                            type="button"
                            onClick={go}
                            className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        >
                            Перейти на {siteVersion}
                        </button>
                        <button
                            type="button"
                            onClick={stay}
                            className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:border-blue-500 hover:bg-blue-50"
                        >
                            Остаться {currentWhere}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
